import React, { useState } from 'react';
import { Row, Col, Card, Button, Form, Alert, Spinner, Badge, Modal } from 'react-bootstrap';

const MeliOrder = () => {
    // State for order management
    const [orderId, setOrderId] = useState('');
    const [trackingNumber, setTrackingNumber] = useState('');
    const [orderData, setOrderData] = useState(null);
    const [shippingLabel, setShippingLabel] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [labelLoading, setLabelLoading] = useState(false);
    const [searchType, setSearchType] = useState('order_id'); // 'order_id' or 'tracking_number'
    const [showLabelModal, setShowLabelModal] = useState(false);
    const [receiveLoading, setReceiveLoading] = useState(false);

    const clearScreen = () => {
        setOrderId('');
        setTrackingNumber('');
        setOrderData(null);
        setShippingLabel(null);
        setError(null);
        setLoading(false);
        setLabelLoading(false);
        setSearchType('order_id');
        setShowLabelModal(false);
        setReceiveLoading(false);
    };

    const searchOrder = async () => {
        const searchValue = searchType === 'order_id' ? orderId.trim() : trackingNumber.trim();

        if (!searchValue) {
            setError(`Please enter a ${searchType === 'order_id' ? 'MELI Order ID' : 'Tracking Number'}`);
            return;
        }

        setLoading(true);
        setError(null);
        setOrderData(null);
        setShippingLabel(null);
        setShowLabelModal(false);

        try {
            let url;
            if (searchType === 'order_id') {
                url = `https://h4gqwxd22m.execute-api.us-east-1.amazonaws.com/beta/integration/orders/${searchValue}`;
            } else {
                url = `https://h4gqwxd22m.execute-api.us-east-1.amazonaws.com/beta/integration/orders?tracking_number=${searchValue}`;
            }

            const response = await fetch(url);
            const data = await response.json();

            if (response.ok && data.success) {
                setOrderData(data.data);
            } else {
                setError(data.message || `${searchType === 'order_id' ? 'Order' : 'Orders'} not found`);
            }
        } catch (err) {
            setError(`Failed to fetch ${searchType === 'order_id' ? 'order' : 'orders'}. Please try again.`);
            console.error('Error fetching data:', err);
        } finally {
            setLoading(false);
        }
    };

    const printLabel = async (order) => {
        if (!order || !order.meli_order_id) {
            setError('Invalid order data');
            return;
        }

        setLabelLoading(true);
        setError(null);
        setShowLabelModal(true); // Show modal immediately with spinner

        try {
            const response = await fetch(
                `https://h4gqwxd22m.execute-api.us-east-1.amazonaws.com/beta/integration/orders/${order.meli_order_id}?shipping_label=true`
            );

            const result = await response.json();

            if (result.success && result.shipping_label) {
                setShippingLabel(result.shipping_label);
            } else if (result.shipping_label_error) {
                // Display the error with debug info
                const errorMessage = result.shipping_label_error.message;
                const debugInfo = result.shipping_label_error.debug_info;

                let fullErrorMessage = errorMessage;
                if (debugInfo && debugInfo.response_text) {
                    try {
                        const responseData = JSON.parse(debugInfo.response_text);
                        fullErrorMessage = `${errorMessage}\n\nMELI API Response: ${responseData.message || debugInfo.response_text}`;
                    } catch (e) {
                        fullErrorMessage = `${errorMessage}\n\nMELI API Response: ${debugInfo.response_text}`;
                    }
                }

                setError(fullErrorMessage);
            } else {
                setError('Failed to get shipping label');
            }
        } catch (err) {
            setError(`Error: ${err.message}`);
        } finally {
            setLabelLoading(false);
        }
    };

    const receiveInWarehouse = async (order) => {
        if (!order || !order.meli_order_id) {
            setError('Invalid order data');
            return;
        }

        setReceiveLoading(true);
        setError(null);

        try {
            const response = await fetch(
                `https://h4gqwxd22m.execute-api.us-east-1.amazonaws.com/beta/integration/orders/${order.meli_order_id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        status: 'RECEIVED_IN_WAREHOUSE'
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                // Update the order data with the returned data
                if (result.data) {
                    if (Array.isArray(orderData)) {
                        // Multiple orders - update the specific order in the array
                        const updatedOrders = orderData.map(o =>
                            o.meli_order_id === order.meli_order_id ? result.data[0] : o
                        );
                        setOrderData(updatedOrders);
                    } else {
                        // Single order - replace with updated data
                        setOrderData(result.data[0]);
                    }
                }
                setError(null);
            } else {
                setError(`Failed to update order: ${result.message || result.error}`);
            }
        } catch (err) {
            setError(`Error updating order: ${err.message}`);
        } finally {
            setReceiveLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleString();
        } catch {
            return dateString;
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'PENDING': { variant: 'warning', text: 'Pending' },
            'PROCESSING': { variant: 'info', text: 'Processing' },
            'SHIPPED': { variant: 'success', text: 'Shipped' },
            'DELIVERED': { variant: 'primary', text: 'Delivered' },
            'CANCELLED': { variant: 'danger', text: 'Cancelled' },
            'RECEIVED_IN_WAREHOUSE': { variant: 'info', text: 'Received in Warehouse' }
        };

        const config = statusConfig[status] || { variant: 'secondary', text: status };
        return <Badge bg={config.variant} style={{ fontSize: '1.5em' }}>{config.text}</Badge>;
    };

    const renderOrderDetails = (order, index) => (
        <Card key={order.meli_order_id || index} className="mb-3 bg-dark text-white">
            <Card.Header className="d-flex flex-column">
                <h4 className="mb-0">{order.meli_order_id}</h4>
                {order.fulfillment_status && getStatusBadge(order.fulfillment_status)}
            </Card.Header>
            <Card.Body>
                {order.meli_order_status === 'cancelled' && (
                    <Alert variant="danger" className="mb-3" style={{ fontSize: '1.2em', fontWeight: 'bold', textAlign: 'center' }}>
                        ORDER CANCELLED BY CUSTOMER
                    </Alert>
                )}
                <hr />

                {/* RECEIVE IN WAREHOUSE Button */}
                <div className="mb-3">
                    <Button
                        variant="success"
                        onClick={() => receiveInWarehouse(order)}
                        disabled={receiveLoading || order.fulfillment_status !== 'SHIPPED'}
                        className="me-2"
                    >
                        {receiveLoading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Updating...
                            </>
                        ) : (
                            '📦 Receive In Warehouse'
                        )}
                    </Button>
                </div>
                <div className="mb-3">

                    <Button
                        variant="primary"
                        onClick={() => printLabel(order)}
                        disabled={labelLoading}
                    >
                        {labelLoading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Generating...
                            </>
                        ) : (
                            'PRINT LABEL'
                        )}
                    </Button>
                </div>

                <hr />

                {/* Single Column Layout */}
                <div>
                    <h6 className="text-muted mb-3">Order Information</h6>
                    <p><strong>Order ID:</strong> {order.meli_order_id}</p>
                    <p><strong>Customer:</strong> {order.firstname} {order.lastname} ({order.nickname || 'N/A'})</p>
                    <p><strong>Pack ID:</strong> {order.meli_pack_id || 'N/A'}</p>
                    <p><strong>Order GSX:</strong> {order.order_gsx || 'N/A'}</p>

                    <p><strong>Received:</strong> {formatDate(order.received_dateTime)}</p>
                    <p><strong>Tracking Number:</strong> {order.tracking_number || 'N/A'}</p>
                    <p><strong>Fulfillment Status:</strong> {order.fulfillment_status || 'N/A'}</p>
                    {order.received_in_warehouse && order.fulfillment_status === 'RECEIVED_IN_WAREHOUSE' && (
                        <p><strong>Received in Warehouse:</strong> {formatDate(order.received_in_warehouse)}</p>
                    )}
                </div>

                <hr />

                <div>
                    <h6 className="text-muted mb-3">Product Details</h6>
                    <p>{order.title || 'N/A'}</p>
                    <p><strong>SKU:</strong> {order.sku}</p>
                    <p><strong>Marketplace ID:</strong> {order.marketplace_id}</p>
                </div>

                <hr />

                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <Button
                            variant="primary"
                            onClick={() => printLabel(order)}
                            disabled={labelLoading}
                        >
                            {labelLoading ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Generating...
                                </>
                            ) : (
                                'PRINT LABEL'
                            )}
                        </Button>
                    </div>
                </div>
            </Card.Body>
        </Card>
    );

    return (
        <>
            <div className="container-fluid">
                <Row>
                    <Col>
                        <Card className="bg-dark text-white">
                            <Card.Header>
                                <h3>🔍 MELI Order Search</h3>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Search Type</Form.Label>
                                            <div>
                                                <Form.Check
                                                    type="radio"
                                                    id="search-order-id"
                                                    name="searchType"
                                                    label="Order ID"
                                                    checked={searchType === 'order_id'}
                                                    onChange={() => setSearchType('order_id')}
                                                    className="text-white"
                                                />
                                                <Form.Check
                                                    type="radio"
                                                    id="search-tracking"
                                                    name="searchType"
                                                    label="Tracking Number"
                                                    checked={searchType === 'tracking_number'}
                                                    onChange={() => setSearchType('tracking_number')}
                                                    className="text-white"
                                                />
                                            </div>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                {searchType === 'order_id' ? 'MELI Order ID' : 'Tracking Number'}
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder={`Enter ${searchType === 'order_id' ? 'Order ID' : 'Tracking Number'}`}
                                                value={searchType === 'order_id' ? orderId : trackingNumber}
                                                onChange={(e) => {
                                                    if (searchType === 'order_id') {
                                                        setOrderId(e.target.value);
                                                    } else {
                                                        setTrackingNumber(e.target.value);
                                                    }
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="d-flex gap-2">
                                    <Button
                                        variant="primary"
                                        onClick={searchOrder}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Searching...
                                            </>
                                        ) : (
                                            'SEARCH'
                                        )}
                                    </Button>
                                    <Button variant="secondary" onClick={clearScreen}>
                                        CLEAR
                                    </Button>
                                </div>

                                {error && (
                                    <Alert variant="danger" className="mt-3">
                                        <Alert.Heading>Error</Alert.Heading>
                                        <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.9em' }}>
                                            {error}
                                        </pre>
                                    </Alert>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {orderData && (
                    <Row className="mt-4">
                        <Col>
                            <Card className="bg-dark text-white">
                                <Card.Header>
                                    <h4>📦 Order Details</h4>
                                </Card.Header>
                                <Card.Body>
                                    {Array.isArray(orderData) ? (
                                        // Multiple orders (pack)
                                        orderData.map((order, index) => renderOrderDetails(order, index))
                                    ) : (
                                        // Single order (backward compatibility)
                                        renderOrderDetails(orderData, 0)
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Shipping Label Modal */}
                <Modal show={showLabelModal} onHide={() => setShowLabelModal(false)} size="md" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>🏷️ Shipping Label</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="text-center">
                        {labelLoading ? (
                            <div>
                                <Spinner animation="border" variant="primary" className="mb-3" />
                                <p>Generating shipping label...</p>
                            </div>
                        ) : shippingLabel ? (
                            <div>
                                <p className="mb-2"><strong>Shipping ID:</strong> {shippingLabel.shipping_id}</p>
                                <p className="mb-2"><strong>File Size:</strong> {(shippingLabel.file_size / 1024).toFixed(2)} KB</p>
                                <p className="mb-2"><strong>Expires in:</strong> {Math.floor(shippingLabel.expires_in / 60)} minutes</p>
                                <Button
                                    variant="success"
                                    size="lg"
                                    href={shippingLabel.pdf_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-100"
                                >
                                    📄 Open PDF Label
                                </Button>
                            </div>
                        ) : error ? (
                            <div>
                                <Alert variant="danger">
                                    <Alert.Heading>Label Generation Failed</Alert.Heading>
                                    <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.9em', textAlign: 'left' }}>
                                        {error}
                                    </pre>
                                </Alert>
                            </div>
                        ) : null}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowLabelModal(false)}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </>
    );
};

export default MeliOrder;
